import type { paths as backendPaths } from "@/client/backend/zz_schema";
import createClient from "openapi-fetch";

type BackendClientOptions = NonNullable<Parameters<typeof createClient>[0]>;

// A successful response can reach the browser with an empty body and no
// `Content-Length: 0` header, e.g. when an upstream proxy re-encodes it with
// chunked transfer encoding. openapi-fetch then calls `response.json()` on an
// empty string and throws "Unexpected end of JSON input".
//
// This guard normalizes any empty 2xx body into an explicit `Content-Length: 0`
// response, which openapi-fetch already treats as `{ data: undefined }`. It runs
// for every request, so empty-body endpoints cannot reintroduce this class of bug.
const fetchWithEmptyBodyGuard = async (request: Request): Promise<Response> => {
  const response = await fetch(request);
  // openapi-fetch already short-circuits 204 / HEAD / Content-Length: 0. The crash
  // only happens for a successful response that has no length header, so that is
  // the only case we need to inspect.
  if (!response.ok || response.headers.get("Content-Length") !== null) {
    return response;
  }
  const isEmpty = (await response.clone().text()).length === 0;
  if (!isEmpty) {
    return response;
  }
  const headers = new Headers(response.headers);
  headers.set("Content-Length", "0");
  return new Response(null, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
};

export function createBackendClient(options: BackendClientOptions) {
  return createClient<backendPaths>({
    ...options,
    // The session may live in an httpOnly cookie instead of a header, so
    // every request carries credentials plus the custom header that
    // CSRF-guards cookie authentication on the backend.
    credentials: "include",
    headers: {
      "X-Limrun-Client": "console",
      ...options.headers,
    },
    fetch: fetchWithEmptyBodyGuard,
  });
}
